// <shopify-account> (native Shopify web component): open the account popover on hover, and
// stop its scroll lock from changing the page width.
//
// Drives the HOST's public API (showModal()/close()) — never reaches into the shadow DOM to
// wire behaviour. The shadow dialog is only READ (open state + bounding box) because that's
// the only place its geometry exists.
//
// Two quirks this code exists to handle:
// 1. Once the modal <dialog> is open, its top-layer backdrop retargets pointer events to the
//    host, so :hover/pointerleave are useless — both host and dialog report "hovered" from
//    anywhere on the page. Hold-open is therefore a document-level `pointermove` geometric
//    hit-test against the icon's and panel's boxes, with a short grace timer to survive the
//    ~30px gap the pointer crosses between icon and panel.
// 2. On open the component sets inline `overflow: hidden` on <html>. Chrome drops the viewport
//    scrollbar despite the component's own inline `scrollbar-gutter: stable`, so the viewport
//    width jumps by the scrollbar width (measured 1265→1280) and the right-anchored dialog
//    shifts relative to the header. Fix: while open, keep the scrollbar with inline
//    `overflow-y: scroll` on <html> and block wheel/touch scrolling instead. Deliberately NOT
//    Motto's `.scroll-locked` body class — `position: fixed` on <body> un-sticks the sticky
//    header (it snaps back to its natural offset), visibly moving the header under the open
//    panel. Blocking the scroll inputs mutates no layout, so nothing moves and there is no
//    scroll offset to save/restore. Keyboard scrolling needs no handler (focus is trapped in
//    the modal), and a scrollbar drag can't happen while open (the pointer travelling there
//    leaves both hit-test boxes and closes the panel first).
//    (`body { scrollbar-gutter: stable }` in base.css already stops body content shifting.)

// Shopify's own popover/drawer switch for this component is 751px — NOT our $grid-* tokens —
// so the override tracks the vendored component, not the brand grid.
const DESKTOP_POPOVER_MQ = window.matchMedia('(min-width: 751px)');

// Grace period for the pointer to cross the gap between the icon and the panel, and slack
// around both boxes so a 1px overshoot doesn't flap the panel shut.
const CLOSE_GRACE_MS = 200;
const BOX_SLACK_PX = 8;

function withinRect(rect, x, y) {
  return (
    x >= rect.left - BOX_SLACK_PX &&
    x <= rect.right + BOX_SLACK_PX &&
    y >= rect.top - BOX_SLACK_PX &&
    y <= rect.bottom + BOX_SLACK_PX
  );
}

function initAccountHover(host) {
  const dialog = host.shadowRoot && host.shadowRoot.querySelector('dialog');
  if (!dialog) return;

  let closeTimer = null;
  let locked = false;

  function clearCloseTimer() {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  function preventScroll(event) {
    event.preventDefault();
  }

  function onPointerMove(event) {
    // Guard: only hit-test while the panel is actually open — a closed dialog's box is
    // stale/elsewhere and reads as "outside", arming a spurious close on the icon→panel dive.
    if (!dialog.hasAttribute('open')) return;
    const inside =
      withinRect(host.getBoundingClientRect(), event.clientX, event.clientY) ||
      withinRect(dialog.getBoundingClientRect(), event.clientX, event.clientY);
    if (inside) {
      clearCloseTimer();
    } else if (!closeTimer) {
      closeTimer = setTimeout(() => {
        closeTimer = null;
        if (dialog.hasAttribute('open')) host.close();
      }, CLOSE_GRACE_MS);
    }
  }

  function lockWithoutWidthJump() {
    locked = true;
    // Set AFTER the component's own inline `overflow: hidden` (we run from the attribute
    // mutation, i.e. after showModal) — a later inline longhand wins over the shorthand.
    // Keeping the scrollbar re-enables wheel scrolling, so block the inputs instead.
    document.documentElement.style.overflowY = 'scroll';
    document.addEventListener('wheel', preventScroll, {
      passive: false,
      capture: true,
    });
    document.addEventListener('touchmove', preventScroll, {
      passive: false,
      capture: true,
    });
  }

  function unlock() {
    if (!locked) return; // opened in sub-751px drawer mode — we never locked
    locked = false;
    document.removeEventListener('wheel', preventScroll, { capture: true });
    document.removeEventListener('touchmove', preventScroll, { capture: true });
    document.documentElement.style.overflowY = '';
  }

  function onOpenChanged() {
    if (dialog.hasAttribute('open')) {
      if (!DESKTOP_POPOVER_MQ.matches) return; // sub-751px drawer mode: leave untouched
      lockWithoutWidthJump();
      document.addEventListener('pointermove', onPointerMove);
    } else {
      document.removeEventListener('pointermove', onPointerMove);
      clearCloseTimer();
      unlock();
    }
  }

  // One observer handles hover-opens, click-opens, and every close path (ESC, backdrop,
  // in-panel close button) — the `open` attribute is the single source of truth.
  new MutationObserver(onOpenChanged).observe(dialog, {
    attributes: true,
    attributeFilter: ['open'],
  });

  host.addEventListener('pointerenter', (event) => {
    if (event.pointerType !== 'mouse') return; // touch taps already open via click
    if (!DESKTOP_POPOVER_MQ.matches) return;
    if (!dialog.hasAttribute('open')) host.showModal();
  });
}

export default function initShopifyAccountHover() {
  // Wait for the component to upgrade — its shadow root (and the dialog) doesn't exist before.
  customElements.whenDefined('shopify-account').then(() => {
    document.querySelectorAll('shopify-account').forEach(initAccountHover);
  });
}

// Self-initialising side-effect import (matches the sibling modules in this folder).
initShopifyAccountHover();
